import { createContext, useContext } from 'react';
import { createStore, useStore as useZustandStore } from 'zustand';

import { ArticleSlice, getArticleInitialState } from './article';
import { BISlice, getBIInitialState } from './bi';
import {
  CategoriesTreeSlice,
  getCategoriesTreeInitialState,
} from './categories-tree';
import { CategorySlice, getCategoryInitialState } from './category';
import { CopilotSlice, getCopilotInitialState } from './copilot';
import { DeviceSlice, getDeviceInitialState } from './device';
import {
  FeatureTogglesSlice,
  getFeatureTogglesInitialState,
} from './feature-toggles';
import { LoaderSlice, getLoaderInitialState } from './loader';
import { PageSlice, getPageInitialState } from './page';
import { ReleasesSlice, getReleasesInitialState } from './releases';
import { SessionSlice, getSessionInitialState } from './session';
import {
  SiteSelectorSlice,
  getSiteSelectorInitialState,
} from './site-selector';
import { UserSlice, getUserInitialState } from './user';

export type Store = {
  articleSlice: ArticleSlice;
  categoriesTreeSlice: CategoriesTreeSlice;
  categorySlice: CategorySlice;
  biSlice: BISlice;
  deviceSlice: DeviceSlice;
  featureTogglesSlice: FeatureTogglesSlice;
  copilotSlice: CopilotSlice;
  loaderSlice: LoaderSlice;
  pageSlice: PageSlice;
  releasesSlice: ReleasesSlice;
  sessionSlice: SessionSlice;
  siteSelectorSlice: SiteSelectorSlice;
  userSlice: UserSlice;
};

type PreloadedState = {
  [K in keyof Store]?: Partial<Store[K]>;
};

export type StoreType = ReturnType<typeof initializeStore>;

const storeContext = createContext<StoreType | null>(null);

export const Provider = storeContext.Provider;

export const useStore = <T>(selector: (state: Store) => T): T => {
  const store = useContext(storeContext);
  if (!store) {
    throw new Error('Store is missing the provider');
  }
  return useZustandStore(store, selector);
};

export const getInitialState = (
  preloadedState: PreloadedState,
  set: (stateUpdater: (state: Store) => Partial<Store>) => void
) => ({
  articleSlice: {
    ...getArticleInitialState(set),
    ...preloadedState?.articleSlice,
  },
  categoriesTreeSlice: {
    ...getCategoriesTreeInitialState(),
    ...preloadedState?.categoriesTreeSlice,
  },
  categorySlice: {
    ...getCategoryInitialState(set),
    ...preloadedState?.categorySlice,
  },
  biSlice: {
    ...getBIInitialState(set),
    ...preloadedState?.biSlice,
  },
  deviceSlice: {
    ...getDeviceInitialState(set),
    ...preloadedState?.deviceSlice,
  },
  featureTogglesSlice: {
    ...getFeatureTogglesInitialState(),
    ...preloadedState?.featureTogglesSlice,
  },
  releasesSlice: {
    ...getReleasesInitialState(),
    ...preloadedState?.releasesSlice,
  },
  copilotSlice: {
    ...getCopilotInitialState(set),
    ...preloadedState?.copilotSlice,
  },
  loaderSlice: {
    ...getLoaderInitialState(set),
    ...preloadedState?.loaderSlice,
  },
  pageSlice: {
    ...getPageInitialState(set),
    ...preloadedState?.pageSlice,
  },
  sessionSlice: {
    ...getSessionInitialState(set),
    ...preloadedState?.sessionSlice,
  },
  siteSelectorSlice: {
    ...getSiteSelectorInitialState(set),
    ...preloadedState?.siteSelectorSlice,
  },
  userSlice: {
    ...getUserInitialState(),
    ...preloadedState?.userSlice,
  },
});

export const initializeStore = (preloadedState: PreloadedState = {}) =>
  createStore<Store>()((set) => getInitialState(preloadedState, set) as Store);
